/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import dayjs from 'dayjs';
import { minutesOfDay } from '@features/agenda/utils/agenda-dates';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';

export const DAY_START_HOUR = 0;

/**
 * Minimum rendered height for event chips — ensures two lines of text
 * (time range at 10px + title at 11px + vertical padding) remain visible
 * without being clipped by overflow-hidden.
 */
export const MIN_CHIP_HEIGHT = 38;

/**
 * Minimum chip height for DayView — accounts for larger font sizes
 * (11px + 13px) and additional padding (py-1.5 = 12px total).
 */
export const MIN_CHIP_HEIGHT_DAY = 44;

/**
 * Computes the vertical placement of a timed event inside an hour grid.
 * @param event - The expanded event to place.
 * @param slotHeight - Pixel height of a one-hour slot.
 * @param minHeight - Minimum rendered chip height in pixels.
 * @returns The `top` offset and `height` in pixels.
 */
export const eventPlacement = (
  event: ExpandedEvent,
  slotHeight: number,
  minHeight: number
): { top: number; height: number } => {
  const startMin = minutesOfDay(event.start_at_utc) - DAY_START_HOUR * 60;
  const durationMin = dayjs(event.end_at_utc).diff(
    dayjs(event.start_at_utc),
    'minute'
  );
  const rawTop = (startMin / 60) * slotHeight;
  const rawHeight = (durationMin / 60) * slotHeight;
  // Events starting before DAY_START_HOUR would get a negative `top`,
  // rendering above the scrollable grid's origin where they can't be
  // scrolled to or clicked — clip to the grid's start instead.
  return {
    top: Math.max(0, rawTop),
    height: Math.max(rawHeight + Math.min(0, rawTop), minHeight),
  };
};

/**
 * Formats an event's start–end span as `HH:mm – HH:mm`.
 * @param event - The expanded event.
 * @returns The formatted time range string.
 */
export const timeRange = (event: ExpandedEvent): string => {
  const start = dayjs(event.start_at_utc).format('HH:mm');
  const end = dayjs(event.end_at_utc).format('HH:mm');
  return `${start} às ${end}`;
};

/**
 * Whether an event occupies the whole day and therefore has no meaningful
 * position on the hour grid.
 * @param event - The expanded event.
 * @returns True when the event is flagged as all-day.
 */
export const isAllDayEvent = (event: ExpandedEvent): boolean =>
  Boolean(event.is_all_day);

/**
 * Formats an event's time for display, collapsing all-day events to their
 * label instead of the meaningless `00:00 – 23:59` span.
 * @param event - The expanded event.
 * @param allDayLabel - Translated "all day" label.
 * @returns The label to show next to the event title.
 */
export const eventTimeLabel = (event: ExpandedEvent, allDayLabel: string): string =>
  isAllDayEvent(event) ? allDayLabel : timeRange(event);

/**
 * Splits events into the all-day band and the timed hour grid.
 * @param events - Events already filtered to the relevant day(s).
 * @returns The all-day events and the timed ones, in input order.
 */
export const splitAllDay = (
  events: ExpandedEvent[]
): { allDay: ExpandedEvent[]; timed: ExpandedEvent[] } => ({
  allDay: events.filter(isAllDayEvent),
  timed: events.filter((e) => !isAllDayEvent(e)),
});

/**
 * Computes the vertical offset (px) of the current-time indicator for `date`.
 * @param date - The day the grid column represents.
 * @param slotHeight - Pixel height of a one-hour slot.
 * @param hoursVisible - Number of hours the grid displays.
 * @returns The offset in pixels, or null when not today or off-grid.
 */
export const nowOffset = (
  date: Date,
  slotHeight: number,
  hoursVisible = 24
): number | null => {
  const now = dayjs();
  if (!now.isSame(dayjs(date), 'day')) return null;
  const minutesFromStart = now.hour() * 60 + now.minute() - DAY_START_HOUR * 60;
  if (minutesFromStart < 0 || minutesFromStart > hoursVisible * 60) return null;
  return (minutesFromStart / 60) * slotHeight;
};
